// [PURPOSE]: Centralized registry of class features with mechanical effects.
// [WHY]: srdCharacterData.js stores features as strings only ('Extra Attack',
// 'Rage', etc.). This file maps each named feature to its effects array so the
// effectRegistry can register and consume them uniformly.
// [INTEGRATION]: useClassFeatureEffects hook reads this on class/level change
// and registers effects with prefix 'class:<ClassName>:<FeatureName>'.

/**
 * Class feature effect registry.
 *
 * Schema: SRD_CLASS_FEATURES[className][featureName] = {
 *   minLevel: number,        // Class level the feature is gained
 *   effects: Effect[],       // Effects to register into effectRegistry
 *   notes?: string,          // Optional: rules clarifications, approximations
 * }
 *
 * Effects use the unified schema consumed by effectRegistry.
 */
export const SRD_CLASS_FEATURES = {
  // [Phase 4b bucket 2 — APR 2026]: Warlock base class features.
  Warlock: {
    'Pact Boon': {
      minLevel: 3,
      choiceKey: 'pactBoon',
      choices: {
        'Pact of the Blade': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'warlock_pact_blade_summon_weapon' },
          ],
          notes: 'PHB p.107 — Action: create a magic weapon in your hand (+0 magic; you can bind any magic weapon as your pact weapon). [BREADCRUMB] Combat-tracker / inventory hook for summoned pact weapon.',
        },
        'Pact of the Tome': {
          // [Phase B / #045 — MAY 2026]: at-will cantrips moved to a
          //   declarative effect. atWillSpellsFromField pulls each name
          //   from playerStats.pactTomeCantrips (set by the LevelUpWizard
          //   pact-tome-cantrip step) and registers it as an atWillSpell
          //   effect via expandFromFieldEffects in useClassFeatureEffects.
          //   Pre-fix the hook had a hardcoded className/pactBoon branch.
          effects: [
            { type: 'feature', operation: 'grant', value: 'warlock_pact_tome_three_cantrips' },
            { type: 'atWillSpellsFromField', operation: 'grant', sourcePath: 'pactTomeCantrips' },
          ],
          notes: 'PHB p.108 — Book of Shadows: pick 3 cantrips from any class spell list, can cast at-will. Cantrip names live at playerStats.pactTomeCantrips; atWillSpellsFromField in this effects block expands them into per-cantrip atWillSpell registrations.',
        },
        'Pact of the Chain': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'warlock_pact_chain_familiar' },
          ],
          notes: 'PHB p.108 — Familiar: imp / pseudodragon / quasit / sprite (chosen on summon). Find Familiar spell granted. [BREADCRUMB] Familiar tracker; Find Familiar at-will spell grant.',
        },
        'Pact of the Talisman': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'warlock_pact_talisman_d4_ability_check' },
          ],
          notes: 'TCE p.71 — Magic talisman: when wearer fails ability check, can add d4 (PB times per long rest). [BREADCRUMB] Per-rest tracker + ability-check resolution rule.',
        },
      },
      notes: 'PHB p.107 / TCE p.71 — Player picks one Pact Boon at L3. Stored at character.pactBoon. Pact of the Talisman is from TCE expansion.',
    },
    'Mystic Arcanum': {
      minLevel: 11,
      effects: [
        { type: 'feature', operation: 'grant', value: 'warlock_mystic_arcanum_levels_6_to_9' },
      ],
      notes: 'PHB p.108 — Pick 1 warlock spell of 6th level at L11, +1 of 7th at L13, +1 of 8th at L15, +1 of 9th at L17. Each can be cast 1/long rest WITHOUT using a pact magic spell slot. Wired: LevelUpWizard needsHighLevelSpellPick (type: mysticArcanum) → App.jsx highLevelSpellPicks commit (→ playerStats.mysticArcanum[level]).',
    },
    'Eldritch Master': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'warlock_eldritch_master_pact_magic_recovery' },
      ],
      notes: 'PHB p.108 — 1 minute of entreating your patron: regain all expended Pact Magic spell slots. 1/long rest. [BREADCRUMB] RestPanel custom recovery action (similar to Wizard Arcane Recovery but full-restore instead of half-level).',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Wizard base class features.
  Wizard: {
    'Arcane Recovery': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'wizard_arcane_recovery_short_rest_slot_recovery' },
        // [Spec A Task 7 — 2026-05-16]: slotRecoveryAction wires the
        //   RestPanel "Class Actions" button → SlotRecoveryModal flow.
        //   Replaces the [BREADCRUMB] previously documented in the notes
        //   below. Pool: classResources Wizard arcane_recovery (1/long_rest).
        { type: 'slotRecoveryAction', operation: 'grant', value: {
            actionName: 'Arcane Recovery',
            budgetFormula: 'ceil(classLevel/2)',
            maxSlotTier: 5,
            spellSlotPool: 'spellSlots',
            classKey: 'Wizard',
            poolResourceId: 'arcane_recovery',
        } },
      ],
      notes: 'PHB p.115 — Once per day after a short rest, recover spell slots up to half your wizard level (rounded up); none higher than 5th level. Resource via classResources Wizard arcane_recovery (1/long rest). Wired Spec A Task 7: slotRecoveryAction effect → RestPanel Class Actions → SlotRecoveryModal.',
    },
    'Spell Mastery': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'wizard_spell_mastery_at_will_1st_2nd' },
        // [Spec A Task 8 — 2026-05-16]: atWillSpell with pickedAtLevelUp
        //   resolves picks from playerStats.customClassPicks.Wizard.spell_mastery
        //   (populated by routeHighLevelSpellPicks in App.jsx). SpellbookTab
        //   queries effectCalculator.getAtWillSpells which surfaces these
        //   names with "Cast (no slot)" buttons.
        { type: 'atWillSpell', operation: 'grant', value: {
            pickedAtLevelUp: true,
            className: 'Wizard',
            featureId: 'spell_mastery',
            pickConstraints: { count: 2, forcedLevels: [1, 2], fromSpellbookOnly: true },
        } },
      ],
      notes: 'PHB p.115 — Pick 1 first-level + 1 second-level wizard spell from your spellbook; cast at will. Wired Spec A Task 5+6+8: LevelUpWizard subPicks → App.jsx routeHighLevelSpellPicks → playerStats.customClassPicks.Wizard.spell_mastery → effectCalculator.getAtWillSpells emits.',
    },
    'Signature Spells': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'wizard_signature_spells_3rd_level_twice' },
        // [Spec A Task 8 — 2026-05-16]: perRestSpell with pickedAtLevelUp.
        //   usesPerDay: 1, rechargeOn: 'short_rest' per PHB p.115.
        //   Consumption tracked in playerStats._perRestSpellUses (existing
        //   shared map; consumePerRestSpellUse helper decrements; RestPanel
        //   short/long rest handlers reset).
        { type: 'perRestSpell', operation: 'grant', value: {
            pickedAtLevelUp: true,
            className: 'Wizard',
            featureId: 'signature_spells',
            pickConstraints: { count: 2, forcedLevels: [3], fromSpellbookOnly: true },
            usesPerDay: 1,
            rechargeOn: 'short_rest',
        } },
      ],
      notes: 'PHB p.115 — Pick 2 third-level wizard spells from spellbook; cast each once without slot, recovering on short/long rest. Wired Spec A Task 5+6+8: LevelUpWizard subPicks → App.jsx routeHighLevelSpellPicks → playerStats.customClassPicks.Wizard.signature_spells → effectCalculator.getPerRestSpells emits with usesPerDay=1 / rechargeOn=short_rest.',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Sorcerer base class features.
  Sorcerer: {
    'Font of Magic': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'sorcerer_font_of_magic_slot_conversion' },
      ],
      notes: 'PHB p.99 — Sorcery points (resource via classResources Sorcerer sorcery_points, already wired). Bonus action: convert sorcery points ↔ spell slots. [BREADCRUMB] Spell-slot conversion UI in spellbook tab; sorcery_points ↔ slot table per PHB p.101.',
    },
    'Metamagic': {
      minLevel: 3,
      effects: [
        { type: 'feature', operation: 'grant', value: 'sorcerer_metamagic_options_available' },
      ],
      notes: "PHB p.101 — Pick 2 Metamagic options at L3 (Careful/Distant/Empowered/Extended/Heightened/Quickened/Subtle/Twinned), 1 more at L10, 1 more at L17 (4 total). Each costs sorcery points when applied to a spell. Wired: LevelUpWizard needsMetamagicPicks step → App.jsx metamagicPicks commit (→ playerStats.metamagicOptions array). Cast-time application UI is a separate concern.",
    },
    'Sorcerous Restoration': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'sorcerer_sorcerous_restoration_short_rest_4_points' },
      ],
      notes: 'PHB p.101 — Regain 4 sorcery points at end of short rest (1/short rest). [BREADCRUMB] RestPanel hook checks this feature flag and adds to sorcery_points recharge.',
    },
  },

  // [Q16 — 2026-05-18]: Cleric base class features. Pre-fix, Cleric was the
  //   ONLY full-caster missing from this registry (Wizard / Sorcerer / Druid /
  //   Bard / Warlock all present from Phase 4b bucket 2). The Q16 compliance
  //   audit (May 2026) caught it: every Cleric feature was silently inert —
  //   srdCharacterData.js named them as strings ('Spellcasting',
  //   'Channel Divinity (1/rest)', 'Destroy Undead (CR 1/2)', etc.) but
  //   useClassFeatureEffects had nothing to register because this object
  //   was missing the Cleric key. classResources.js already had the
  //   channel_divinity pool (maxTable: { 2: 1, 6: 2, 18: 3 }) wired since
  //   Phase 4b, so resource accounting was correct; only the per-feature
  //   effect registration was absent.
  //   Feature-name keys MUST match the strings in srdCharacterData.js
  //   features[level] arrays exactly — useClassFeatureEffects looks up by
  //   that name. PHB p.56-59 + SRD 5.1 anchor.
  Cleric: {
    'Spellcasting': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_spellcasting_full_caster_wis' },
      ],
      notes: 'PHB p.58 — Full caster (WIS-based). Prepared list = WIS mod + cleric level. Cantrips: 3/4/5 at L1/L4/L10 (already in srdCharacterData spellcasting block; effectCalculator reads casterType:full).',
    },
    'Divine Domain': {
      minLevel: 1,
      effects: [
        // [BREADCRUMB] Domain-specific features (Bonus Proficiency, Disciple
        //   of Life, Warding Flare, etc.) live in srdSubclassFeatures /
        //   subclass registry — each Cleric domain enumerates its own
        //   per-level features. This entry only records that L1 is where
        //   the player picks their domain (stored at character.divineDomain).
        //   Mirrors how Sorcerous Origin / Bard College / etc. are handled
        //   in their respective base-class entries above.
        { type: 'feature', operation: 'grant', value: 'cleric_divine_domain_picked' },
      ],
      choiceKey: 'divineDomain',
      notes: 'PHB p.58 — Player picks a Divine Domain at L1. 7 SRD options: Knowledge / Life / Light / Nature / Tempest / Trickery / War (srdCharacterData.subclasses block, line 508+). Each domain grants its own per-level features (Bonus Proficiency at L1, Channel Divinity option at L2, capstone-style improvements at L6/L8/L17). Cleric is unusual among picks-at-L1 subclasses in that the L2 Channel Divinity ALSO grants a domain-specific Channel Divinity option (e.g., Preserve Life for Life Domain) — that wiring lives in the subclass registry, not here. [BREADCRUMB] Subclass-feature registration is a separate effort that needs to span all 7 domains.',
    },
    'Channel Divinity (1/rest)': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_channel_divinity_available' },
      ],
      notes: 'PHB p.58-59 — Channel Divinity: spend a use to invoke a divine effect. Cleric always knows Turn Undead; domains grant a second option. Resource via classResources Cleric channel_divinity (already wired pre-Q16, maxTable { 2: 1, 6: 2, 18: 3 }, short_rest recharge). The (1/rest) name encodes the L2 baseline; the L6 + L18 improvements are separate entries below (they share the feature flag but bump the maxTable; the resource layer handles the use-count scaling, this layer just signals availability).',
    },
    'Domain feature': {
      minLevel: 2,
      effects: [],
      notes: "PHB p.58 — Placeholder entry. srdCharacterData.features[2/6/8/17] each list 'Domain feature' — the actual per-level domain features live in the subclass registry (see srdCharacterData.subclasses[domain].features[level]). This empty entry exists so useClassFeatureEffects doesn't warn about an unregistered feature name. [BREADCRUMB] Subclass-feature wiring will replace this placeholder with real effects per domain per level.",
    },
    'Destroy Undead (CR 1/2)': {
      minLevel: 5,
      // [Q16 — 2026-05-18]: PHB p.59 Destroy Undead CR cap table.
      //   When a Cleric uses Turn Undead, undead of CR ≤ this value are
      //   destroyed instead of fleeing. The table lives here for any
      //   consumer (combat tracker, future turn-undead resolver) that
      //   needs the math at runtime; mirrors Barbarian Rage's
      //   rageDamageByLevel data-block pattern.
      destroyUndeadCrByLevel: [
        { atLevel: 5,  cr: 0.5 },
        { atLevel: 8,  cr: 1   },
        { atLevel: 11, cr: 2   },
        { atLevel: 14, cr: 3   },
        { atLevel: 17, cr: 4   },
      ],
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_destroy_undead_cr_0_5' },
      ],
      notes: 'PHB p.59 — When you use Turn Undead, undead of CR ≤ 1/2 are destroyed (instead of merely turned). See destroyUndeadCrByLevel for the full scaling (L5→1/2, L8→1, L11→2, L14→3, L17→4). [BREADCRUMB] Combat-tracker / turn-undead resolver should read the highest-tier feature flag present (cleric_destroy_undead_cr_*) OR consume destroyUndeadCrByLevel directly with the cleric level.',
    },
    'Destroy Undead (CR 1)': {
      minLevel: 8,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_destroy_undead_cr_1' },
      ],
      notes: 'PHB p.59 — At L8, Destroy Undead CR cap improves to 1.',
    },
    'Destroy Undead (CR 2)': {
      minLevel: 11,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_destroy_undead_cr_2' },
      ],
      notes: 'PHB p.59 — At L11, Destroy Undead CR cap improves to 2.',
    },
    'Destroy Undead (CR 3)': {
      minLevel: 14,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_destroy_undead_cr_3' },
      ],
      notes: 'PHB p.59 — At L14, Destroy Undead CR cap improves to 3.',
    },
    'Destroy Undead (CR 4)': {
      minLevel: 17,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_destroy_undead_cr_4' },
      ],
      notes: 'PHB p.59 — At L17, Destroy Undead CR cap improves to 4 (highest tier).',
    },
    'Channel Divinity (2/rest)': {
      minLevel: 6,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_channel_divinity_2_uses' },
      ],
      notes: 'PHB p.58-59 — At L6, Channel Divinity uses scale to 2/short rest. Use-count handled by classResources channel_divinity maxTable; this flag is for any consumer that needs to know the tier explicitly (e.g., UI badges, achievement triggers).',
    },
    'Channel Divinity (3/rest)': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_channel_divinity_3_uses' },
      ],
      notes: 'PHB p.58-59 — At L18, Channel Divinity uses scale to 3/short rest. Use-count handled by classResources channel_divinity maxTable.',
    },
    'Divine Intervention': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_divine_intervention_available' },
      ],
      notes: "PHB p.59 — Action: call on your deity to intervene. Roll d100; if result ≤ cleric level, deity intervenes (DM adjudicates the effect — RAW deliberately leaves this open). On success, can't use again for 7 days. On failure, can try again after next long rest. [BREADCRUMB] Per-character cooldown tracker (7-day success / next-long-rest failure) — separate from classResources because the recharge is RESULT-dependent, not flat. Activity log entry on use + result.",
    },
    'Divine Intervention improvement': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'cleric_divine_intervention_auto_success' },
      ],
      notes: 'PHB p.59 — At L20, Divine Intervention succeeds automatically (no d100 roll). 7-day cooldown still applies. The consumer that handles divine_intervention_available should check for this flag and bypass the d100 step.',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Druid base class features.
  Druid: {
    'Druidic': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_druidic_secret_language' },
      ],
      notes: 'PHB p.66 — Secret druid language. Narrative — surfaces in CharacterBasicsPanel languages list.',
    },
    'Wild Shape': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_wild_shape_available' },
      ],
      notes: 'PHB p.66 — Action: transform into a beast you have seen. CR/swim/fly restrictions scale by level (PHB p.67 table). Resource via classResources Druid wild_shape (added in this commit, max 2, short_rest recharge).',
    },
    'Wild Shape improvement': {
      minLevel: 4,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_wild_shape_cr_scaling' },
      ],
      notes: 'PHB p.66 — At L4, Wild Shape CR cap improves to 1/2 (no fly), L8 CR 1 (no swim/fly restriction). Note: srdCharacterData entries at L4 + L8 each emit "Wild Shape improvement" but the registry collapses to a single entry — the L4 minLevel; combat panel reads druid level for the actual CR cap.',
    },
    'Timeless Body': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_timeless_body_aging_slow' },
      ],
      notes: 'PHB p.67 — Age 1/10 normal rate; magic cannot age you. Narrative.',
    },
    'Beast Spells': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_beast_spells_cast_in_wild_shape' },
      ],
      notes: 'PHB p.67 — Cast druid spells while in Wild Shape (V/S replaced with growls/gestures). [BREADCRUMB] Combat-tracker spell-cast eligibility check.',
    },
    'Archdruid': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'druid_archdruid_unlimited_wild_shape' },
      ],
      notes: 'PHB p.67 — Unlimited Wild Shape uses; ignore V/S/non-consumed M components on druid spells. Surfaces via hasFeatureFlag — classResources Druid wild_shape resource consumer should treat the flag as "max = infinite" at L20.',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Bard base class features.
  Bard: {
    'Bardic Inspiration': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_bardic_inspiration_die_grant' },
      ],
      notes: "PHB p.53 — Bonus action: grant a creature an inspiration die (d6/d8/d10/d12 by tier) usable on attack/check/save within 10 minutes. Resource via classResources Bard bardic_inspiration (max = max(1, CHA mod), short_rest_at_5 conditional recharge). dieSize scales d6 (L1) → d8 (L5) → d10 (L10) → d12 (L15). [BREADCRUMB] Combat-tracker reaction-to-aid + ally-buff aura.",
    },
    'Jack of All Trades': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_jack_of_all_trades' },
      ],
      notes: 'PHB p.54 — Add ⌊proficiency bonus / 2⌋ to ANY ability check that does NOT already include proficiency. Surfaces via the existing calculateSkillBonus path (checks playerStats.features OR registry feature flag, see effectCalculator.js line ~1217). Also applied to initiative (PHB Sage Advice).',
    },
    'Song of Rest': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_song_of_rest_die_grant' },
      ],
      notes: 'PHB p.54 — During short rest, allies that spend hit dice get extra HP equal to a bardic die roll (d6 L2 / d8 L9 / d10 L13 / d12 L17). [BREADCRUMB] RestPanel hook + die-size lookup based on Bard level.',
    },
    'Expertise': {
      minLevel: 3,
      effects: [],
      notes: "PHB p.54 — Pick 2 skills you're proficient in; proficiency bonus is doubled for them. Repeated at L10 with 2 more. Wired: LevelUpWizard needsExpertisePicks step (Bard L3/L10, any proficient skill) → App.jsx expertisePicks commit (→ playerStats.expertiseSkills; effectRegistry proficiencyLevel:2).",
    },
    'Font of Inspiration': {
      minLevel: 5,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_font_of_inspiration' },
      ],
      notes: 'PHB p.54 — Bardic Inspiration recharges on short rest (in addition to long rest). Wired via classResources Bard bardic_inspiration conditionalRecharge=short_rest_at_5.',
    },
    'Countercharm': {
      minLevel: 6,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_countercharm_aura' },
      ],
      notes: "PHB p.54 — Action: until end of next turn, you and creatures within 30 ft have advantage on saves vs being frightened or charmed. [BREADCRUMB] Aura + duration tracking (advantage applies to friendlies, not registered as self-only flag).",
    },
    'Magical Secrets': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_magical_secrets_picker_available' },
      ],
      notes: 'PHB p.54 — Choose 2 spells from any class to learn. Repeated at L14 (+2 more) and L18 (+2 more). Wired: LevelUpWizard needsHighLevelSpellPick (type: magicalSecrets, classFilter:null, any class) → App.jsx highLevelSpellPicks commit (→ playerStats.magicalSecrets array).',
    },
    'Superior Inspiration': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'bard_superior_inspiration_initiative_recharge' },
      ],
      notes: 'PHB p.54 — When you roll initiative and have 0 Bardic Inspiration uses, regain 1 use. [BREADCRUMB] Combat-start trigger handler (similar to Tireless Spirit pattern).',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Barbarian base class features expanded
  // beyond the original Bucket 2 work in 81c2ef9 (Rage + Extra Attack only).
  Barbarian: {
    'Reckless Attack': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_reckless_attack' },
      ],
      notes: 'PHB p.49 — On your turn, attack recklessly: advantage on STR-based melee weapon attack rolls until your next turn, but attacks AGAINST you also have advantage. [BREADCRUMB] Combat-tracker per-turn toggle (sets recklessAttackActive in _activeFlags). Affects both your attacks AND incoming attacks for the round — handler is reciprocal.',
    },
    'Danger Sense': {
      minLevel: 2,
      effects: [
        // RAW PHB p.49: advantage on DEX saves vs effects you can SEE (dragon breath,
        // Fireball, etc.). Cannot be blinded/deafened/incapacitated.
        // The "can-see" condition is too narrow for a passive context flag — we
        // register a generic save:dexterity advantage and the combat panel filters
        // by visibility at save-time. This may over-grant for non-visible effects;
        // breadcrumbed.
        { type: 'advantage', operation: 'advantage', target: 'save:dexterity' },
        { type: 'feature', operation: 'grant', value: 'barbarian_danger_sense_visible_only' },
      ],
      notes: 'PHB p.49 — Advantage on DEX saves vs effects you can SEE (not blinded/deafened/incapacitated). [BREADCRUMB] Visibility filter on the advantage is combat-tracker concern. Current schema over-grants for non-visible effects — feature flag tells consumers to filter.',
    },
    'Fast Movement': {
      minLevel: 5,
      effects: [
        { type: 'speed', operation: 'add', value: 10, condition: { when: 'notWearingHeavyArmor' } },
      ],
      notes: 'PHB p.49 — +10 speed when not wearing heavy armor. Conditional via notWearingHeavyArmor context flag (added to _buildContext alongside this entry) — the negated form is the cleanest way to express "applies UNLESS heavy armor" without consumer-side hardcoding.',
    },
    'Feral Instinct': {
      minLevel: 7,
      effects: [
        { type: 'advantage', operation: 'advantage', target: 'initiative' },
        { type: 'feature', operation: 'grant', value: 'barbarian_feral_instinct_surprised_can_rage' },
      ],
      notes: 'PHB p.49 — Advantage on initiative; if surprised at start of combat, can act normally on your turn IF you rage first. The advantage on initiative consumed by hasAdvantageOnCheck via target:initiative. [BREADCRUMB] Surprise-handling flag for combat-tracker.',
    },
    'Brutal Critical (1 die)': {
      minLevel: 9,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_brutal_critical_1_extra_die', condition: { when: 'criticalHit' } },
      ],
      notes: 'PHB p.49 — On a critical hit with a melee weapon, roll one additional weapon damage die. Conditional on criticalHit context flag (set by combat panel at the moment of a confirmed crit, foundation 510d5ad). [BREADCRUMB] Combat-tracker reads the feature flag to know how many extra dice; entry is the L9 baseline.',
    },
    'Brutal Critical (2 dice)': {
      minLevel: 13,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_brutal_critical_2_extra_dice', condition: { when: 'criticalHit' } },
      ],
      notes: 'PHB p.49 — At L13, +2 extra weapon damage dice on crit (replaces L9 +1).',
    },
    'Brutal Critical (3 dice)': {
      minLevel: 17,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_brutal_critical_3_extra_dice', condition: { when: 'criticalHit' } },
      ],
      notes: 'PHB p.49 — At L17, +3 extra weapon damage dice on crit. Combat-tracker should pick the highest-tier flag present.',
    },
    'Relentless Rage': {
      minLevel: 11,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_relentless_rage_con_save_drop_to_1' },
      ],
      notes: 'PHB p.49 — While raging, when reduced to 0 HP without being killed outright: CON save (DC 10 first time, +5 each subsequent) — on success, drop to 1 HP instead. Resets on long rest. [BREADCRUMB] HP-zero handler + DC tracker.',
    },
    'Persistent Rage': {
      minLevel: 15,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_persistent_rage_no_unconscious_end' },
      ],
      notes: 'PHB p.49 — Rage no longer ends from not attacking or taking damage; ends only if knocked unconscious or you choose to end it. [BREADCRUMB] Rage-duration handler in combat-tracker.',
    },
    'Indomitable Might': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_indomitable_might_str_check_floor' },
      ],
      notes: 'PHB p.49 — On a STR check, treat the result as your STR score if it would be lower. [BREADCRUMB] Skill/check-roll resolver applies the floor when the feature flag is present (similar to Rogue Reliable Talent treats-9-as-10 pattern).',
    },
    'Primal Champion': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'barbarian_primal_champion_str_con_plus_4_max_24' },
      ],
      notes: 'PHB p.49 — Strength +4 (max 24); Constitution +4 (max 24). Wired: App.jsx handleLevelUpComplete auto-mutates STR/CON +4 and sets statCaps to 24 when Barbarian reaches L20 (no wizard step — auto-mutation only).',
    },
    'Rage': {
      minLevel: 1,
      // [Phase B / #042 — MAY 2026]: Damage tier moved to a data block.
      //   Pre-fix, effectRegistry._getValue had a hardcoded
      //   `if (lvl >= 16) return 4; if (lvl >= 9) return 3; ...` ladder
      //   gated on `value === 'rage_damage_bonus'`. The ladder now lives
      //   here as `rageDamageByLevel`; the resolver looks it up via
      //   resolveByLevelTable. Strings go against god — and so did the
      //   ladder.
      // PHB p.48: +2 (L1), +3 (L9), +4 (L16).
      rageDamageByLevel: [
        { atLevel: 1, value: 2 },
        { atLevel: 9, value: 3 },
        { atLevel: 16, value: 4 },
      ],
      effects: [
        { type: 'damageBonus', operation: 'add', value: 'rage_damage_bonus', condition: { when: 'raging' } },
      ],
      notes: 'Damage scales by Barbarian level (+2 L1, +3 L9, +4 L16) — see rageDamageByLevel data block. Active only when raging — UI must toggle the condition flag.',
    },
    'Extra Attack': {
      minLevel: 5,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
    },
  },
  Fighter: {
    'Fighting Style': {
      minLevel: 1,
      choiceKey: 'fightingStyle',
      choices: {
        'Archery': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'fighter_fighting_style_archery' },
          ],
          notes: 'PHB p.72 — +2 attack rolls with ranged weapons. [BREADCRUMB] Combat-tracker reads the feature flag at attack-time (per-attack weapon shape — passive context can\'t determine ranged vs melee weapon being used).',
        },
        'Defense': {
          effects: [
            { type: 'ac', operation: 'add', value: 1, condition: { when: 'wearingAnyArmor' } },
          ],
          notes: 'PHB p.72 — While wearing armor, +1 AC. Conditional via wearingAnyArmor context flag.',
        },
        'Dueling': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'fighter_fighting_style_dueling' },
          ],
          notes: 'PHB p.72 — +2 damage with one-handed melee weapon when no other weapons. [BREADCRUMB] Combat-tracker reads the feature flag at hit-time.',
        },
        'Great Weapon Fighting': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'fighter_fighting_style_great_weapon' },
          ],
          notes: 'PHB p.72 — Reroll 1s/2s on melee weapon damage with two-handed/versatile (used 2H). [BREADCRUMB] Combat-tracker damage-resolution rule.',
        },
        'Protection': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'fighter_fighting_style_protection' },
          ],
          notes: 'PHB p.72 — Reaction with shield: impose disadvantage on an attack against a creature within 5 ft. [BREADCRUMB] Reaction handler.',
        },
        'Two-Weapon Fighting': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'fighter_fighting_style_two_weapon' },
          ],
          notes: 'PHB p.72 — Add ability mod to off-hand damage. [BREADCRUMB] Combat-tracker damage-resolution rule (off-hand attack rendering).',
        },
      },
      notes: 'PHB p.72 — Pick one Fighting Style at L1. Stored at character.fightingStyle. Fighter is the only class that gets all 6 styles; Paladin is missing Archery/Two-Weapon, Ranger is missing Two-Weapon (gets Defense/Dueling/GreatWeapon/Archery instead).',
    },
    'Second Wind': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'fighter_second_wind_bonus_action_heal' },
      ],
      notes: 'PHB p.72 — Bonus action: regain 1d10 + Fighter level HP. Resource via classResources Fighter second_wind (max 1, short rest recharge — already wired).',
    },
    'Action Surge': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'fighter_action_surge_extra_action' },
      ],
      notes: 'PHB p.72 — On your turn, take an additional action. Resource via classResources Fighter action_surge (1/short rest at L2, 2/short rest at L17 — already wired).',
    },
    'Extra Attack': {
      minLevel: 5,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
    },
    'Indomitable': {
      minLevel: 9,
      effects: [
        { type: 'feature', operation: 'grant', value: 'fighter_indomitable_reroll_save' },
      ],
      notes: 'PHB p.72 — Reroll a failed save. 1 use at L9, 2 at L13, 3 at L17. Long rest recharge. Resource added to classResources Fighter indomitable (this commit). [BREADCRUMB] Combat-tracker reaction-style trigger after a failed save.',
    },
    'Extra Attack (2)': {
      minLevel: 11,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
      notes: 'Fighter gains a third attack at L11. Stacks with Extra Attack from L5.',
    },
    'Extra Attack (3)': {
      minLevel: 20,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
      notes: 'Fighter gains a fourth attack at L20. Stacks with Extra Attack from L5 + 11.',
    },
  },
  Monk: {
    'Extra Attack': {
      minLevel: 5,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
    },
    // Martial Arts die scaling — wire as unarmedDamage 'set' at appropriate levels.
    // Each level entry is an independent effect; effectCalculator.getUnarmedStrikeDamageDie
    // picks the highest die.
    'Martial Arts (1d4)': {
      minLevel: 1,
      effects: [{ type: 'unarmedDamage', operation: 'set', value: '1d4' }],
    },
    'Martial Arts (1d6)': {
      minLevel: 5,
      effects: [{ type: 'unarmedDamage', operation: 'set', value: '1d6' }],
    },
    'Martial Arts (1d8)': {
      minLevel: 11,
      effects: [{ type: 'unarmedDamage', operation: 'set', value: '1d8' }],
    },
    'Martial Arts (1d10)': {
      minLevel: 17,
      effects: [{ type: 'unarmedDamage', operation: 'set', value: '1d10' }],
    },
    // [Phase 4b bucket 2 — APR 2026]: Monk base class features
    'Unarmored Movement': {
      minLevel: 2,
      effects: [
        // RAW PHB p.78: +10 speed at L2, +15 L6, +20 L10, +25 L14, +30 L18.
        // Conditional: not wearing armor + not using a shield. _buildContext.unarmored
        // covers no-armor; noShield covers shield. Combine via an explicit flag set.
        { type: 'speed', operation: 'add', value: 10, condition: { when: 'unarmored' } },
      ],
      notes: 'PHB p.78 — +10 speed while not wearing armor or using a shield. Scales: +10 L2, +15 L6, +20 L10, +25 L14, +30 L18 (each step is a separate entry below). Note: condition uses unarmored only (no-shield is implicit per Monk RAW; a Monk wielding a shield is non-RAW and breaks the AC formula too).',
    },
    'Unarmored Movement +5': {
      minLevel: 6,
      effects: [
        { type: 'speed', operation: 'add', value: 5, condition: { when: 'unarmored' } },
      ],
      notes: 'PHB p.78 — Adds +5 to base Unarmored Movement (L2 +10) → +15 total at L6.',
    },
    'Unarmored Movement +10': {
      minLevel: 10,
      effects: [
        { type: 'speed', operation: 'add', value: 5, condition: { when: 'unarmored' } },
      ],
      notes: 'PHB p.78 — Stacks with prior tiers to reach +20 at L10.',
    },
    'Unarmored Movement +15': {
      minLevel: 14,
      effects: [
        { type: 'speed', operation: 'add', value: 5, condition: { when: 'unarmored' } },
      ],
      notes: 'PHB p.78 — Stacks to +25 at L14.',
    },
    'Unarmored Movement +20': {
      minLevel: 18,
      effects: [
        { type: 'speed', operation: 'add', value: 5, condition: { when: 'unarmored' } },
      ],
      notes: 'PHB p.78 — Stacks to +30 at L18 (also wall-walking + water-walking — feature flag added in Unarmored Movement improvement narrative below).',
    },
    'Unarmored Movement improvement': {
      minLevel: 9,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_unarmored_movement_wall_water_walk' },
      ],
      notes: 'PHB p.78 — At L9: move along vertical surfaces and across liquids without falling during the move. Feature flag (movement-rule narrative).',
    },
    'Deflect Missiles': {
      minLevel: 3,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_deflect_missiles_reaction' },
      ],
      notes: 'PHB p.78 — Reaction: reduce ranged weapon attack damage by 1d10 + DEX mod + Monk level. If reduced to 0, can spend 1 ki to make a ranged attack with the projectile (or your fist). [BREADCRUMB] Reaction handler.',
    },
    'Slow Fall': {
      minLevel: 4,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_slow_fall_reaction' },
      ],
      notes: 'PHB p.78 — Reaction: reduce falling damage by 5 × Monk level. [BREADCRUMB] Falling-damage handler.',
    },
    'Stunning Strike': {
      minLevel: 5,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_stunning_strike_ki_spend' },
      ],
      notes: 'PHB p.78 — On melee weapon hit, spend 1 ki to force CON save (DC 8+prof+WIS) or be stunned until end of next turn. [BREADCRUMB] Combat-tracker per-attack toggle + ki spend.',
    },
    // [Q19 FIX 2026-05-16]: Ki-spend menu features (PHB p.78). At L2 a Monk
    //   gains Ki AND three options to spend it on: Flurry of Blows, Patient
    //   Defense, Step of the Wind. Pre-fix, none of these were registered as
    //   class features — only the ki_points resource pool existed in
    //   classResources. Adding all three as breadcrumb feature flags so
    //   downstream surfacing (ClassResourcesPanel spend buttons, combat
    //   tracker action-economy) can query them via effectRegistry. Each is
    //   a 1-ki bonus action.
    'Flurry of Blows': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_flurry_of_blows_ki_spend' },
      ],
      notes: 'PHB p.78 — Immediately after taking the Attack action, spend 1 ki as a bonus action to make two unarmed strikes. Spend surface: ClassResourcesPanel Ki Points pool → "Spend Options" pane (Spec B Task 5+10). Open Hand Technique (Way of the Open Hand L3) modifies this — combat-tracker integration deferred.',
    },
    // [Spec B Task 10 — 2026-05-16]: Generic Ki spend menu attached to the
    //   ki_points pool. The individual Flurry / Patient / Step / Stunning
    //   feature entries keep their feature-grant breadcrumbs; THIS entry
    //   surfaces them as expandable "Spend Options" rows in ClassResources
    //   Panel via the spendActionMenu effect type. Subclass features can
    //   extend by registering additional spendActionMenu effects targeting
    //   the same ki_points resource (e.g., Way of Shadow's Shadow Arts).
    'Ki Spend Menu': {
      minLevel: 2,
      effects: [
        { type: 'spendActionMenu', operation: 'grant', value: {
            menuId: 'monk_ki_spend',
            attachedToResource: 'ki_points',
            classKey: 'Monk',
            options: [
              { name: 'Flurry of Blows', cost: 1, minLevel: 2,
                description: 'Immediately after Attack action, bonus action: 2 unarmed strikes (PHB p.78).' },
              { name: 'Patient Defense',  cost: 1, minLevel: 2,
                description: 'Bonus action: take the Dodge action (PHB p.78).' },
              { name: 'Step of the Wind', cost: 1, minLevel: 2,
                description: 'Bonus action: Disengage or Dash; jump distance doubled (PHB p.78).' },
              { name: 'Stunning Strike',  cost: 1, minLevel: 5,
                description: 'On melee weapon hit: spend 1 ki; target makes CON save or stunned (PHB p.79).',
                // [Sub-plan A2-classres-consumers — 2026-05-30]: rollSpec signals
                // ClassResourcesPanel to open UsableAbilityModal BEFORE decrementing.
                // dc:null → ClassResourcesPanel computes at runtime: 8 + prof + WIS.
                // halfOnSave:false — stunning is all-or-nothing (PHB p.79).
                // [Rule 11]: uses rollSpec property-flag — no name-string check.
                rollSpec: {
                  rolls: [],
                  save: { ability: 'constitution', dc: null, halfOnSave: false },
                  targetMode: 'enemy',
                },
                // Signals which DC formula to use (ClassResourcesPanel reads this).
                rollSpecDcFormula: 'ki_save_dc',
              },
            ],
        } },
      ],
      notes: 'PHB p.78-79 — Ki spend menu attached to ki_points pool. Surfaces in ClassResourcesPanel "Spend Options" expandable pane per Spec B Task 5+10. Subclass features extend by registering additional spendActionMenu effects targeting the same attachedToResource.',
    },
    'Patient Defense': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_patient_defense_ki_spend' },
      ],
      notes: 'PHB p.78 — Spend 1 ki as a bonus action to take the Dodge action. [BREADCRUMB] Bonus-action handler + ki spend + Dodge state.',
    },
    'Step of the Wind': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_step_of_the_wind_ki_spend' },
      ],
      notes: 'PHB p.78 — Spend 1 ki as a bonus action to take the Disengage or Dash action; jump distance is doubled for the turn. [BREADCRUMB] Bonus-action handler + ki spend + Disengage/Dash + jump-doubled flag.',
    },
    'Ki-Empowered Strikes': {
      minLevel: 6,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_ki_empowered_strikes_magical_attack' },
      ],
      notes: 'PHB p.78 — Unarmed strikes count as magical for resistance/immunity bypass. Feature flag pattern (parallel to Way of Kensei magical attacks, Beast Bestial Soul, Circle of the Moon Primal Strike).',
    },
    'Evasion': {
      minLevel: 7,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_evasion_dex_save_no_damage' },
      ],
      notes: 'PHB p.78 — On DEX save vs effect that does half-on-success, take 0 on success and half on failure. Same mechanic as Rogue Evasion.',
    },
    'Stillness of Mind': {
      minLevel: 7,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_stillness_of_mind_action_end_charm_fear' },
      ],
      notes: 'PHB p.78 — Action: end one charm or frightened condition on yourself. [BREADCRUMB] Action-economy + condition-removal handler.',
    },
    'Purity of Body': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_purity_of_body_poison_disease_immunity' },
      ],
      notes: 'PHB p.78 — Immune to disease and poison. [BREADCRUMB] Condition-immunity schema (parallel to other condition immunities — feature flag for now).',
    },
    'Tongue of the Sun and Moon': {
      minLevel: 13,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_tongue_sun_moon_understand_all_languages' },
      ],
      notes: 'PHB p.79 — Understand any spoken language; any creature that understands at least one language can understand you. Narrative — surfaces in CharacterBasicsPanel.',
    },
    'Diamond Soul': {
      minLevel: 14,
      effects: [
        // Proficiency in ALL saving throws — the new save:all category-wide grant
        // landed in commit cc81835 (Bucket 2 generalized getGrantedProficiencyLevel).
        // Single effect covers all 6 ability saves via the categoryAll match.
        { type: 'proficiency', operation: 'grant', value: 'save:all', proficiencyLevel: 1 },
        { type: 'feature', operation: 'grant', value: 'monk_diamond_soul_reroll_failed_save_ki' },
      ],
      notes: 'PHB p.79 — Proficiency in all saving throws (save:all category grant — first SRD use of this pattern). Plus: spend 1 ki to reroll a failed save. [BREADCRUMB] Reroll-save UI handler reads the feature flag.',
    },
    'Timeless Body': {
      minLevel: 15,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_timeless_body_no_aging_no_food' },
      ],
      notes: 'PHB p.79 — Cannot be aged magically; needs no food or water. Narrative.',
    },
    'Empty Body': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_empty_body_invisibility_resistance_4_ki' },
      ],
      notes: 'PHB p.79 — Spend 4 ki: invisibility for 1 minute + resistance to all damage except force. [BREADCRUMB] Invisibility tracker + temp-buff resistance application.',
    },
    'Perfect Self': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'monk_perfect_self_initiative_4_ki_recovery' },
      ],
      notes: 'PHB p.79 — When you roll initiative and have 0 ki, regain 4 ki. [BREADCRUMB] Combat-start trigger handler.',
    },
  },
  Paladin: {
    'Divine Sense': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_divine_sense_celestial_fiend_undead_detect' },
      ],
      notes: "PHB p.84 — Action: until end of next turn, know location/distance of celestials/fiends/undead within 60 ft (not behind total cover). 1 + CHA modifier uses per long rest. [BREADCRUMB] Per-rest tracker via classResources entry; combat-panel/exploration trigger.",
    },
    'Lay on Hands': {
      minLevel: 1,
      effects: [],
      notes: "PHB p.84 — Healing pool of HP = 5 × Paladin level. Touch a creature, restore HP. Spend 5 HP to cure disease/poison. Resource tracked via classResources Paladin lay_on_hands (already wired pre-Bucket-2).",
    },
    'Fighting Style': {
      minLevel: 2,
      choiceKey: 'fightingStyle',
      choices: {
        'Defense': {
          effects: [
            { type: 'ac', operation: 'add', value: 1, condition: { when: 'wearingAnyArmor' } },
          ],
          notes: 'PHB p.84 — While wearing armor, +1 AC. Conditional via wearingAnyArmor context flag.',
        },
        'Dueling': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'paladin_fighting_style_dueling' },
          ],
          notes: 'PHB p.84 — +2 damage with one-handed melee weapon when no other weapons. [BREADCRUMB] Combat-tracker reads the feature flag at attack-time (per-attack weapon shape — not in passive context).',
        },
        'Great Weapon Fighting': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'paladin_fighting_style_great_weapon' },
          ],
          notes: 'PHB p.84 — Reroll 1s/2s on melee weapon damage with two-handed/versatile (used 2H). [BREADCRUMB] Combat-tracker damage-resolution rule.',
        },
        'Protection': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'paladin_fighting_style_protection' },
          ],
          notes: 'PHB p.84 — Reaction with shield: impose disadvantage on an attack against a creature within 5 ft. [BREADCRUMB] Reaction handler.',
        },
      },
      notes: 'PHB p.84 — Pick one Fighting Style (Defense/Dueling/Great Weapon Fighting/Protection — Paladin does NOT get Archery, Two-Weapon Fighting, or Blessed Warrior). Stored at character.fightingStyle.',
    },
    'Divine Smite': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_divine_smite_available' },
      ],
      notes: "PHB p.85 — When you hit a creature with a melee weapon attack, you can expend a spell slot to deal 2d8 + 1d8 per slot level above 1st (max 5d8) radiant damage. +1d8 vs undead/fiends. [BREADCRUMB BC-3] Combat panel needs: (1) per-attack 'declare smite + slot level' UI; (2) damage rendering using slotLevel formula. The feature flag exposes availability; the actual damage is per-attack player-toggled (similar to Sneak Attack but with slot-level scaling). Schema for this kind of one-shot toggle damage may need extension — currently encoded as feature flag + breadcrumb.",
    },
    'Divine Health': {
      minLevel: 3,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_divine_health_disease_immunity' },
      ],
      notes: "PHB p.85 — Immune to disease (condition immunity, narrative — surfaces via hasFeatureFlag). Diseases are DM-driven; immunity is a feature flag combat panel/exploration consults.",
    },
    'Extra Attack': {
      minLevel: 5,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
    },
    'Aura of Protection': {
      minLevel: 6,
      effects: [
        // Self saves: +CHA mod (min +1) to all 6 saves. RAW PHB p.85.
        // Implemented as save:<ability> bonus per ability with cha_modifier formula.
        // [BREADCRUMB] min-+1 floor not modeled (most paladins have CHA mod >= 1).
        { type: 'save:strength',     operation: 'add', value: 'cha_modifier' },
        { type: 'save:dexterity',    operation: 'add', value: 'cha_modifier' },
        { type: 'save:constitution', operation: 'add', value: 'cha_modifier' },
        { type: 'save:intelligence', operation: 'add', value: 'cha_modifier' },
        { type: 'save:wisdom',       operation: 'add', value: 'cha_modifier' },
        { type: 'save:charisma',     operation: 'add', value: 'cha_modifier' },
        // Aura also grants the same bonus to allies within 10 ft. NOT modeled
        // (per-ally aura tracking is a future ticket — affects party-sync UI).
        { type: 'feature', operation: 'grant', value: 'paladin_aura_of_protection_radius_10' },
      ],
      notes: "PHB p.85 — Whenever you or a friendly creature within 10 feet must make a saving throw, that creature gains a bonus equal to your CHA mod (min +1). Aura active while paladin is conscious. Self-side wired via 6 save:<ability> effects with cha_modifier formula. [BREADCRUMB] Ally-side aura affecting party members within 10ft is a future PartyPanel ticket. [BREADCRUMB] min-+1 floor (CHA mod < 1) not yet modeled — formula resolver doesn't currently support Math.max(1, ...).",
    },
    'Aura of Courage': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_aura_of_courage_no_frightened' },
      ],
      notes: "PHB p.85 — You and friendly creatures within 10 feet can't be frightened while you are conscious. Self-side modeled as feature flag (combat panel checks before applying frightened). [BREADCRUMB] Ally-side aura within 10ft + condition immunity schema (parallel to damage immunity from Bucket 3) — future tickets.",
    },
    'Improved Divine Smite': {
      minLevel: 11,
      effects: [
        // RAW PHB p.85: "whenever you hit a creature with a melee weapon, the creature
        // takes an extra 1d8 radiant damage." Always-on, no slot spend, per-hit.
        // Conditional on being a melee weapon attack — combat-tracker filters at hit-time.
        // For now, register as add_dice unconditionally; combat-tracker honors the
        // melee restriction. The radiant damage type isn't propagated through the schema;
        // breadcrumb that.
        { type: 'damageBonus', operation: 'add_dice', value: '1d8' },
      ],
      notes: "PHB p.85 — Whenever you hit a creature with a melee weapon, +1d8 radiant damage. Always-on at L11+. Wired as add_dice 1d8 unconditional; combat-tracker filters to melee weapon attacks at hit-time. [BREADCRUMB] Damage type field on damageBonus add_dice (currently the dice formula doesn't carry damage type — combat panel assumes weapon damage type unless rule-specific).",
    },
    'Cleansing Touch': {
      minLevel: 14,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_cleansing_touch_end_spell_on_touch' },
      ],
      notes: "PHB p.85 — Action: end one spell on yourself or a willing creature you touch. CHA modifier uses per long rest (min 1). [BREADCRUMB] Per-rest tracker; spell-removal mechanism in spellCastingService.",
    },
    'Aura improvements': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'paladin_aura_radius_30' },
      ],
      notes: "PHB p.85 — Aura of Protection and Aura of Courage radius increases from 10 ft to 30 ft. Surfaces via hasFeatureFlag for ally-side aura calculations. [BREADCRUMB] Affects PartyPanel aura visualization once that ticket lands.",
    },
  },
  Ranger: {
    'Favored Enemy': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_favored_enemy' },
      ],
      notes: 'PHB p.91 — Pick a favored creature type at L1 (PHB classic) or use Favored Foe (TCE optional class feature). PHB version: advantage on WIS (Survival) checks to track + INT checks to recall info about them. Foe Slayer (L20) adds WIS mod to attack/damage vs them. [BREADCRUMB-WIZARD] Wizard step: prompt favored enemy creature type pick; store at playerStats.favoredEnemy. The advantage condition (vs favored enemy in tracking) is too narrow for _buildContext flag — combat-tracker reads playerStats.favoredEnemy + creature type at action-time.',
    },
    'Favored Enemy improvement': {
      minLevel: 6,
      effects: [],
      notes: 'PHB p.91 — At L6 + L14, pick an additional favored enemy type. Same wizard plumbing as Favored Enemy.',
    },
    'Natural Explorer': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_natural_explorer' },
      ],
      notes: 'PHB p.91 — Pick a favored terrain at L1. INT/WIS skills proficient → double prof (essentially expertise) when in that terrain. Travel benefits (no slow from difficult terrain, full alert, etc.). [BREADCRUMB] Wizard step: terrain pick stored at playerStats.favoredTerrain; expertise-on-skill is effectively conditional on terrain — too situational for passive registry.',
    },
    'Natural Explorer improvement': {
      minLevel: 6,
      effects: [],
      notes: 'PHB p.91 — At L6 + L10, pick an additional favored terrain. Same wizard plumbing as Natural Explorer.',
    },
    'Fighting Style': {
      minLevel: 2,
      choiceKey: 'fightingStyle',
      choices: {
        'Archery': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'ranger_fighting_style_archery' },
          ],
          notes: 'PHB p.91 — +2 attack rolls with ranged weapons. [BREADCRUMB] Combat-tracker reads at attack-time.',
        },
        'Defense': {
          effects: [
            { type: 'ac', operation: 'add', value: 1, condition: { when: 'wearingAnyArmor' } },
          ],
          notes: 'PHB p.91 — While wearing armor, +1 AC.',
        },
        'Dueling': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'ranger_fighting_style_dueling' },
          ],
          notes: 'PHB p.91 — +2 damage with one-handed melee weapon when no other weapons.',
        },
        'Two-Weapon Fighting': {
          effects: [
            { type: 'feature', operation: 'grant', value: 'ranger_fighting_style_two_weapon' },
          ],
          notes: 'PHB p.91 — Add ability mod to off-hand damage.',
        },
      },
      notes: 'PHB p.91 — Pick one Fighting Style at L2. Stored at character.fightingStyle. Ranger gets Archery/Defense/Dueling/Two-Weapon Fighting (NOT Great Weapon Fighting or Protection).',
    },
    'Primeval Awareness': {
      minLevel: 3,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_primeval_awareness_creature_detect' },
      ],
      notes: 'PHB p.91 — Spend a spell slot to detect favored enemies + certain creature types within 1 mile (6 miles in favored terrain) for slot-level minutes. [BREADCRUMB] Spell-slot expenditure UI in spellbook tab.',
    },
    'Extra Attack': {
      minLevel: 5,
      effects: [{ type: 'extra_attack', operation: 'add', value: 1 }],
    },
    "Land's Stride": {
      minLevel: 8,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_lands_stride_difficult_terrain' },
        { type: 'advantage', operation: 'advantage', target: 'save:plant_restraint_magical' },
      ],
      notes: 'PHB p.91 — Move through nonmagical difficult terrain at full speed; no damage from nonmagical plants; advantage on saves vs magical plant restraint. (Mirrors Druid Circle of the Land equivalent.)',
    },
    'Hide in Plain Sight': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_hide_in_plain_sight_camouflage' },
      ],
      notes: "PHB p.91 — Spend 1 minute camouflaging in natural surroundings: +10 to Stealth checks while not moving. [BREADCRUMB] Activated stealth-buff toggle in skill-roll UI.",
    },
    'Vanish': {
      minLevel: 14,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_vanish_hide_bonus_action_no_track' },
      ],
      notes: "PHB p.91 — Bonus action: Hide. Cannot be tracked by nonmagical means. [BREADCRUMB] Combat-tracker action-economy.",
    },
    'Feral Senses': {
      minLevel: 18,
      effects: [
        { type: 'sense', operation: 'grant', target: 'blindsight', value: 30 },
        { type: 'feature', operation: 'grant', value: 'ranger_feral_senses_no_invisible_advantage' },
      ],
      notes: 'PHB p.91 — Aware of invisible creatures within 30 ft (blindsight 30). Attacker invisibility doesn\'t grant advantage against you; your attacks vs invisible creatures aren\'t at disadvantage. Senses surface in SensesMovementPanel; visibility-rule changes are combat-tracker breadcrumbs.',
    },
    'Foe Slayer': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'ranger_foe_slayer_wis_to_attack_or_damage' },
      ],
      notes: 'PHB p.91 — Add Wisdom modifier to attack OR damage roll against a Favored Enemy (player choice on each attack). [BREADCRUMB] Combat-tracker per-attack toggle when target type matches favoredEnemy.',
    },
  },

  // [Phase 4b bucket 2 — APR 2026]: Rogue base class features. Sneak Attack uses
  // the dice-formula resolver (rogueLevel/2:d6:roundUp) landed in commit 96ba581
  // — gives 1d6 at L1, scaling to 10d6 at L19, all from a single registered effect.
  Rogue: {
    'Sneak Attack': {
      minLevel: 1,
      effects: [
        // [PHB p.96] Once per turn, add extra dice to a weapon attack with finesse
        // OR a ranged weapon AND (advantage OR ally adjacent + you not disadvantaged).
        // The "once per turn / finesse-or-ranged / advantage condition" all collapse
        // into the combat panel's sneakAttackActive flag (player declares).
        // Dice scale: ceil(rogueLevel / 2) d6.
        { type: 'damageBonus', operation: 'add_dice', value: 'rogueLevel/2:d6:roundUp', condition: { when: 'sneakAttackActive' } },
      ],
      notes: "PHB p.96 — Once per turn, +ceil(rogueLevel/2)d6 damage on a weapon attack with finesse or ranged when you have advantage OR an ally is adjacent (and you don't have disadvantage). [BREADCRUMB BC-3] Combat panel toggles sneakAttackActive when player declares Sneak Attack on an attack roll. Dice formula resolves at attack-time via _getValue.",
    },
    'Expertise': {
      minLevel: 1,
      effects: [],
      notes: "PHB p.94 — Pick 2 skills (or 1 skill + thieves' tools) you're proficient in; proficiency bonus is doubled for them. Repeated at L6 with 2 more skills/tools. Wired: LevelUpWizard needsExpertisePicks step (Rogue L1/L6, any proficient skill) → App.jsx expertisePicks commit (→ playerStats.expertiseSkills; effectRegistry proficiencyLevel:2).",
    },
    "Thieves' Cant": {
      minLevel: 1,
      effects: [],
      notes: "PHB p.96 — Secret rogue language (signs + chatter). Narrative — surfaces in CharacterBasicsPanel languages list (already there).",
    },
    'Cunning Action': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_cunning_action_dash_disengage_hide_bonus' },
      ],
      notes: "PHB p.96 — Bonus action: Dash, Disengage, or Hide. [BREADCRUMB] Combat-tracker action-economy — feature flag exposes the option to the combat panel.",
    },
    'Uncanny Dodge': {
      minLevel: 5,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_uncanny_dodge_halve_damage_reaction' },
      ],
      notes: "PHB p.96 — Reaction: when an attacker you can see hits you with an attack, halve the damage. [BREADCRUMB] Reaction handler in combat panel.",
    },
    'Evasion': {
      minLevel: 7,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_evasion_dex_save_no_damage' },
      ],
      notes: "PHB p.96 — On DEX save vs effect that does half-on-success, take 0 on success and half on failure. [BREADCRUMB] DEX save resolution + damage-application handler.",
    },
    'Reliable Talent': {
      minLevel: 11,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_reliable_talent_skill_floor_10' },
      ],
      notes: "PHB p.96 — On any ability check using a skill you're proficient in, treat a d20 roll of 9 or lower as 10. [BREADCRUMB] Skill-check resolution rule — DiceTab + skill-roll consumer needs to clamp the d20 floor when feature flag is present.",
    },
    'Blindsense': {
      minLevel: 14,
      effects: [
        { type: 'sense', operation: 'grant', target: 'blindsense', value: 10 },
      ],
      notes: "PHB p.96 — Aware of the location of any hidden or invisible creature within 10 feet, provided you can hear. Surfaces in SensesMovementPanel via getEffectsForStat('sense').",
    },
    'Slippery Mind': {
      minLevel: 15,
      effects: [
        { type: 'proficiency', operation: 'grant', value: 'save:wisdom', proficiencyLevel: 1 },
      ],
      notes: "PHB p.97 — Proficiency in Wisdom saving throws. Surfaces via getGrantedProficiencyLevel('save:wisdom') in calculateSavingThrow.",
    },
    'Elusive': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_elusive_no_advantage_unless_incapacitated' },
      ],
      notes: "PHB p.97 — No attack roll has advantage against you while you aren't incapacitated. [BREADCRUMB] Combat-tracker advantage-check filter.",
    },
    'Stroke of Luck': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'rogue_stroke_of_luck_one_per_rest' },
      ],
      notes: "PHB p.97 — Once per short/long rest: turn a missed attack into a hit, OR a failed ability check into a 20. [BREADCRUMB] Per-rest tracker via classResources entry (added in this commit) + DiceTab/CombatPanel UI to invoke.",
    },
  },

  // [Q26 FIX 2026-05-16]: Artificer (TCoE p.9-12). Pair-gap with Cleric (Q16)
  //   closed in this commit. Artificer differs structurally from Cleric: the
  //   class lives entirely in `tashas.js` as a source-gated class, but its
  //   feature flags belong here in srdClassFeatures so the SRD-side registration
  //   path (`useClassFeatureEffects`) finds them when Tasha's is enabled.
  //
  //   Spellcasting math (half-caster ROUND UP, INT) lives in
  //   `tashas.js` -> classData.spellcasting.halfCasterRoundUp + effectCalculator
  //   (verified by SPR-007 test suite). The Spellcasting entry below is a
  //   feature-flag breadcrumb only, mirroring the Wizard / Cleric pattern.
  //
  //   The classResources block for Artificer (Flash of Genius, Active
  //   Infusions, Spell-Storing Item) landed in the prior commit; this block
  //   adds the *feature flags* that surface those features.
  Artificer: {
    'Magical Tinkering': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_magical_tinkering' },
      ],
      notes: 'TCoE p.9 — Ribbon feature. Touch a Tiny nonmagical object and imbue one of: light 5-ft radius, recorded message (≤6 sec), odor/sound, or static visual effect. Up to INT-mod objects simultaneously (min 1). [BREADCRUMB] No mechanical computation; flag is for downstream queries.',
    },
    'Spellcasting': {
      minLevel: 1,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_spellcasting' },
      ],
      notes: 'TCoE p.9 — Half-caster, INT-based, prepared spells, ROUND UP for multiclass. Math lives in classData.spellcasting + effectCalculator (halfCasterRoundUp: true). This entry is a flag for downstream queries.',
    },
    'Infuse Item': {
      minLevel: 2,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_infuse_item' },
        // [Spec B Task 12 — 2026-05-16]: longRestPicker drives the
        //   DailySetupModal step where the player assigns active
        //   infusions to inventory items each long rest. The picker
        //   also has pickKnownAtLevelUp so LevelUpWizard runs a step
        //   at L2/6/10/14/18 to let the player learn new infusions
        //   from srdInfusions.SRD_INFUSIONS. Recipes go to
        //   playerStats.classKnownSelections.Artificer.infuse_item;
        //   active bindings go to classActiveSelections.Artificer.infuse_item.
        { type: 'longRestPicker', operation: 'grant', value: {
            pickerId: 'infuse_item',
            knownTable:  { 2: 4, 6: 6, 10: 8, 14: 10, 18: 12 },
            activeTable: { 2: 2, 6: 3, 10: 4, 14: 5, 18: 6 },
            pickSource: 'infusion_list',
            bindTarget: 'item',
            pickKnownAtLevelUp: true,
            classKey: 'Artificer',
        } },
      ],
      notes: 'TCoE p.10 — Learn 4 infusions; have up to 2 active simultaneously. Active count scales: L2 2, L6 3, L10 4, L14 5, L18 6 (tracked via classResources `infuse_item_active`). Recipes-known count is a separate state field (4/6/8/10/12). Wired Spec B Task 12: longRestPicker effect → LevelUpWizard infusion picker step (pickKnownAtLevelUp) + DailySetupModal post-long-rest binding.',
    },
    "The Right Tool for the Job": {
      minLevel: 3,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_right_tool_for_the_job' },
      ],
      notes: 'TCoE p.11 — With 1-hour work using thieves\' tools or artisan\'s tools, magically produce one set of artisan\'s tools in an unoccupied space. Vanishes when used again. Narrative — no game-day mechanical effect.',
    },
    'Tool Expertise': {
      minLevel: 6,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_tool_expertise' },
      ],
      notes: 'TCoE p.11 — Proficiency bonus doubled for ANY ability check using a tool you\'re proficient with. Auto-applies to ALL tools (unlike Rogue Expertise which is picker-based). [BREADCRUMB] SkillsPanel / ability-check handler reads this flag and applies 2× PB on tool checks.',
    },
    'Flash of Genius': {
      minLevel: 7,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_flash_of_genius' },
        // [Spec B Task 11 — 2026-05-16]: reactionTrigger surfaces in
        //   StatsPanel "Reactions" section via effectCalculator.getReactionTriggers.
        //   Click Invoke → decrement flash_of_genius pool by 1 + log.
        //   Player manually adjudicates the +INT mod numerical bonus
        //   (v1 — no combat panel; combat-tracker conditional payloads deferred).
        { type: 'reactionTrigger', operation: 'grant', value: {
            actionName: 'Flash of Genius',
            description: 'Reaction: when you or a creature within 30 ft makes an ability check or save, add your INT modifier to the roll (TCoE p.11).',
            attachedToResource: 'flash_of_genius',
            cost: 1,
            classKey: 'Artificer',
        } },
      ],
      notes: 'TCoE p.11 — Reaction: add INT mod to any ability check or saving throw (yourself or any creature within 30 ft). Uses = INT mod / long rest (min 1) — tracked via classResources `flash_of_genius`. Wired Spec B Task 11: reactionTrigger effect → StatsPanel Reactions section → Invoke button decrements pool + logs.',
    },
    'Magic Item Adept': {
      minLevel: 10,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_magic_item_adept_4_slots' },
      ],
      notes: 'TCoE p.11 — Attune to 4 magic items at once (default 3). Craft uncommon magic items in 1/4 time and 1/2 gold. Attunement cap is consumed by `src/utils/attunementUtils.getMaxAttunement` (data-driven via classes array).',
    },
    'Spell-Storing Item': {
      minLevel: 11,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_spell_storing_item' },
        // [Spec B Task 13 — 2026-05-16]: longRestPicker drives the
        //   DailySetupModal step where the player binds one chosen
        //   spell (level 1 or 2 from the Artificer list) to one
        //   inventory item per long rest. Charges = 2 × INT mod
        //   (tracked via classResources spell_storing_item). The
        //   ItemDialog of the bound item shows a "Cast stored spell"
        //   action that decrements the pool and logs the cast.
        { type: 'longRestPicker', operation: 'grant', value: {
            pickerId: 'spell_storing_item',
            activeTable: { 11: 1 },
            pickSource: 'class_spell_list',
            pickConstraints: { maxSpellLevel: 2, classKey: 'Artificer' },
            bindTarget: 'item',
            chargesFormula: 'intMod*2',
            classKey: 'Artificer',
        } },
      ],
      notes: 'TCoE p.11 — After long rest, touch a weapon or spellcasting focus and imbue with a chosen 1st-or-2nd-level Artificer spell (1-action cast time). Any creature holding the item can cast the stored spell using your spell save DC. Charges = 2 × INT mod, refresh on long rest — tracked via classResources `spell_storing_item`. Wired Spec B Task 13: longRestPicker effect → DailySetupModal post-long-rest binding → ItemDialog "Cast stored spell" action via spellCastingService.castStoredSpell.',
    },
    'Magic Item Savant': {
      minLevel: 14,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_magic_item_savant_5_slots' },
        { type: 'feature', operation: 'grant', value: 'artificer_magic_item_savant_ignore_reqs' },
      ],
      notes: 'TCoE p.12 — Attune to 5 magic items. Ignore all class/race/spell/level attunement requirements (e.g., a Wizard-only staff). Attunement cap is consumed by getMaxAttunement; the ignore-reqs flag is read by the attunement UI gate.',
    },
    'Magic Item Master': {
      minLevel: 18,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_magic_item_master_6_slots' },
      ],
      notes: 'TCoE p.12 — Attune to 6 magic items at once. Consumed by getMaxAttunement.',
    },
    'Soul of Artifice': {
      minLevel: 20,
      effects: [
        { type: 'feature', operation: 'grant', value: 'artificer_soul_of_artifice_save_bonus' },
        { type: 'feature', operation: 'grant', value: 'artificer_soul_of_artifice_death_ward' },
      ],
      notes: 'TCoE p.12 — +1 to ALL saving throws per attuned magic item (dynamic computation: count of attuned items × 1, applied to all 6 saves). 1/long-rest reaction: when reduced to 0 HP, end one infusion to drop to 1 HP instead. [BREADCRUMB] effectCalculator.getSaveModifier dynamic computation — see FIX-10. Death-ward reaction tracker is combat-tracker territory.',
    },
  },
};

// Freeze the per-class entries so callers can't mutate the registry at runtime.
Object.freeze(SRD_CLASS_FEATURES.Barbarian);
Object.freeze(SRD_CLASS_FEATURES.Bard);
Object.freeze(SRD_CLASS_FEATURES.Cleric); // [Q16 FIX 2026-05-18 — pair to Q26 Artificer]
Object.freeze(SRD_CLASS_FEATURES.Druid);
Object.freeze(SRD_CLASS_FEATURES.Fighter);
Object.freeze(SRD_CLASS_FEATURES.Monk);
Object.freeze(SRD_CLASS_FEATURES.Paladin);
Object.freeze(SRD_CLASS_FEATURES.Ranger);
Object.freeze(SRD_CLASS_FEATURES.Rogue);
Object.freeze(SRD_CLASS_FEATURES.Sorcerer);
Object.freeze(SRD_CLASS_FEATURES.Warlock);
Object.freeze(SRD_CLASS_FEATURES.Wizard);
Object.freeze(SRD_CLASS_FEATURES.Artificer); // [Q26 FIX 2026-05-16]
Object.freeze(SRD_CLASS_FEATURES);

/**
 * Returns the array of [featureName, featureData] entries that the character
 * has unlocked for a given class entry (className + level).
 *
 * @param {string} className
 * @param {number} classLevel
 * @returns {Array<[string, object]>}
 */
export function getActiveClassFeatures(className, classLevel) {
  if (!className || typeof classLevel !== 'number') return [];
  const classData = SRD_CLASS_FEATURES[className];
  if (!classData) return [];
  return Object.entries(classData).filter(([_, feature]) => feature.minLevel <= classLevel);
}
